import { createLocationAPI, createMemberAPI } from "../api/apiFactory";
import { isMemberAnAdmin, isMemberAPracticeManager } from "../core/auth";
import { tprStorage } from "../core/storage";
import { RoomService } from "./RoomService";
import type { ILocationAPI } from "../api/locationApi";
import type { Location, LocationFilters, LocationSort } from "../types/Location";
import type { Member } from "../types/Member";
import type { Notice } from "../types/Notice";
import type { PaginatedResponse } from "../types/Pagination";
import type { Amenity, AmenityFilters, AmenitySort } from "@/types/Amenity";

/**
 * Service layer provides higher-level abstractions over the raw API.
 * Use this for:
 * - Client-side filtering/transformations
 * - Combining multiple API calls
 * - Business logic that doesn't belong in the API layer
 * - Caching strategies
 */
export class LocationService {
  public api: ILocationAPI;

  constructor(api?: ILocationAPI) {
    this.api = api || createLocationAPI();
  }

  private applyClientFilters(locations: Location[], filters: LocationFilters): Location[] {
    return locations.filter((location) => {
      if (filters.locationId && location.id !== filters.locationId) return false;
      if (filters.name && location.name !== filters.name) return false;
      if (filters.city && location.city !== filters.city) return false;
      if (filters.isAccessible !== undefined && location.isAccessible !== filters.isAccessible) return false;
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const nameMatch = location.name.toLowerCase().includes(searchLower);
        const cityMatch = location.city.toLowerCase().includes(searchLower);
        if (!nameMatch && !cityMatch) return false;
      }
      return true;
    });
  }

  private sortLocations(locations: Location[], sort: LocationSort): Location[] {
    const sorted = [...locations];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "city":
          comparison = a.city.localeCompare(b.city);
          break;
      }

      return sort.order === "asc" ? comparison : -comparison;
    });

    return sorted;
  }

  private applyAmenityFilters(amenities: Amenity[], filters: AmenityFilters): Amenity[] {
    return amenities.filter((amenity) => {
      if (filters.amenityId && amenity.id !== filters.amenityId) return false;
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const nameMatch = amenity.name.toLowerCase().includes(searchLower);
        const descriptionMatch = amenity.description?.toLowerCase().includes(searchLower);
        const tagMatch = amenity.tags?.some((tag) => tag.toLowerCase().includes(searchLower));
        if (!nameMatch && !descriptionMatch && !tagMatch) return false;
      }
      return true;
    });
  }

  private sortAmenities(amenities: Amenity[], sort: AmenitySort): Amenity[] {
    const sorted = [...amenities];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "isBookable":
          comparison = (a.isBookable ? 1 : 0) - (b.isBookable ? 1 : 0);
          break;
        case "count":
          comparison = (a.count || 0) - (b.count || 0);
          break;
      }

      return sort.order === "asc" ? comparison : -comparison;
    });

    return sorted;
  }

  async getPermissableLocations(): Promise<Array<Location>> {
    const memberApi = createMemberAPI();
    const locationApi = createLocationAPI();
    const storedMember = await Promise.resolve(tprStorage.get("tpruser"));
    if (!storedMember) {
      return [];
    }

    const member = await memberApi.fetchMember();
    if (!member) {
      return [];
    }

    // Practice managers and admins have access to all locations
    const hasAccessToAllLocations =
      isMemberAPracticeManager(member) || isMemberAnAdmin(member);

    if (hasAccessToAllLocations) {
      // Elevated users see ALL locations INCLUDING inactive ones, so they can reach and set up a
      // location during a phased roll-out.
      const response = await locationApi.fetchSerializedLocations(true);
      return response.data;
    }

    // For regular members, return only their permissable locations
    // Check both permissableLocations (array of objects) and permissableLocationIds (array of strings)

    // Regular members must NEVER see inactive locations — drop nulls (not found) AND inactive ones.
    const keepActive = (loc: Location | null): loc is Location => loc !== null && loc.active !== false;

    // Try permissableLocationIds first (array of location IDs)
    if (Array.isArray(member.permissableLocationIds) && member.permissableLocationIds.length > 0) {
      const locations = await Promise.all(
        member.permissableLocationIds.map(async (locId) => {
          const location = await locationApi.fetchLocationById(locId);
          return location || null;
        }),
      );
      return locations.filter(keepActive);
    }

    // Fallback to permissableLocations (array of location objects or IDs)
    if (Array.isArray(member.permissableLocations) && member.permissableLocations.length > 0) {
      const locations = await Promise.all(
        member.permissableLocations.map(async (loc) => {
          const locId = typeof loc === "string" ? loc : loc.id;
          const location = await locationApi.fetchLocationById(locId);
          return location || null;
        }),
      );
      return locations.filter(keepActive);
    }

    return [];
  }

  async getLocationNotices(locationId: string): Promise<Notice[] | []> {
    const memberApi = createMemberAPI();
    const locationApi = createLocationAPI();
    const _location = await locationApi.fetchLocationById(locationId);
    if (!_location) {
      return [];
    }

    for (const notice of _location.notices || []) {
      if (notice.authorId && !notice.author) {
        const member = await memberApi.fetchMemberById(notice.authorId);
        if (member) {
          // Only include safe, public fields
          notice.author = {
            id: member.id,
            firstName: member.firstName,
            lastName: member.lastName,
            avatar: member.avatar,
            businessName: member.businessName,
          };
        }
      }
    }
    return _location.notices || [];
  }

  async getPracticeManagerByLocation(locationId: string): Promise<Array<Member>> {
    return this.api.fetchPracticeManagerByLocation(locationId);
  }

  async getSerializedLocations(includeInactive = false): Promise<PaginatedResponse<Location>> {
    return this.api.fetchSerializedLocations(includeInactive);
  }

  async getLocationsWithRoomData(filters?: LocationFilters, sort?: LocationSort, includeInactive = false): Promise<PaginatedResponse<Location>> {
    return this.getLocations(filters, sort, true, includeInactive);
  }

  async getLocationById(id: string): Promise<Location | null> {
    return this.api.fetchLocationById(id);
  }

  async getAmenityById(locationId: string, amenityId: string): Promise<Amenity | null> {
    return this.api.fetchAmenityById(locationId, amenityId);
  }

  async getAmenitiesForLocation(locationId: string, filters?: AmenityFilters, sort?: AmenitySort): Promise<Amenity[]> {
    let amenities = await this.api.fetchAmenitiesForLocation(locationId);

    if (filters) {
      amenities = this.applyAmenityFilters(amenities, filters);
    }

    if (sort) {
      amenities = this.sortAmenities(amenities, sort);
    }

    return amenities;
  }

  async getLocations(
    filters?: LocationFilters,
    sort?: LocationSort,
    withRoomData: boolean = true,
    includeInactive: boolean = false,
  ): Promise<PaginatedResponse<Location>> {
    const response = await this.api.fetchSerializedLocations(includeInactive);
    let locations = response.data;

    if (filters) {
      locations = this.applyClientFilters(locations, filters);
    }
    if (sort) {
      locations = this.sortLocations(locations, sort);
    }

    if (withRoomData) {
      // Single bulk fetch — previously fanned out to `getAllRoomsForLocation` once per location,
      // producing N requests per admin view. `getAllRooms()` gives us everything in one call and
      // we group by locationId client-side.
      const roomService = new RoomService();
      const allRooms = await roomService.getAllRooms();
      const roomsByLocation = new Map<string, typeof allRooms>();
      for (const room of allRooms) {
        const bucket = roomsByLocation.get(room.locationId);
        if (bucket) {
          bucket.push(room);
        } else {
          roomsByLocation.set(room.locationId, [room]);
        }
      }
      locations = locations.map((location) => ({
        ...location,
        availableRooms: roomsByLocation.get(location.id) ?? [],
      }));
    }

    return {
      ...response,
      data: locations,
    };
  }

  async getPracticeManagedLocations(memberId: string): Promise<Location[]> {
    return this.api.fetchPracticeManagedLocations(memberId);
  }

  // this stops ts complaining about the unused private field
  async noop(): Promise<void> {
    console.log(this.api);
    return;
  }
}
